/* eslint-disable @typescript-eslint/no-floating-promises */
import useQueries from "@/hooks/useQueries";
import Header from "@/shared/components/ui/Header";
import MainLayout from "@/shared/components/ui/MainLayout";
import { buildQuery } from "@/shared/utils/buildQuery";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import Cookies from 'js-cookie';
import { Plus, Printer, Trash } from "lucide-react";
import { useState, ChangeEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface Item {
    item_id: string;
    item_name: string;
    item_type: string;
    barcode: string
}

interface ApiResponse {
    data: {
        result: Item[];
    };
}

interface RowData {
    id: number;
    name: string;
    qty: number;
    showSuggestions: boolean;
    barcode: string
}

const fetchItems = async (licence_id: string, itemsQuery: string): Promise<ApiResponse> => {
    const response = await axios.post<ApiResponse>(
        'https://ntelgnt.com/api/itqan/remote/query/do_query.php',
        { q: itemsQuery },
        {
            headers: { 'Content-Type': 'application/json' },
            params: {
                t: 'sel',
                phone: '',
                id: licence_id,
                s: Cookies.get('sessionId'),
                btn: 'w_itm'
            },
        }
    );
    return response.data;
}

const CreateLabels = () => {
    const { t } = useTranslation();
    const licence_id = localStorage.getItem('licence_id') || '';
    const navigate = useNavigate();

    const queries = useQueries();

    const itemsQuery = useMemo(() =>
        (queries) ? buildQuery(queries.itemsQuery) : '',
        [queries]);

    const { data } = useQuery<ApiResponse>({
        queryKey: ['items', licence_id, itemsQuery],
        queryFn: () => fetchItems(licence_id, itemsQuery)
    });

    const items: Item[] = data?.data?.result || [];

    const [rows, setRows] = useState<RowData[]>([
        { id: Date.now(), name: '', qty: 1, showSuggestions: false, barcode: '' }
    ]);

    const handleInputChange = (index: number, field: keyof RowData, value: string | number | boolean) => {
        const newRows = [...rows];
        (newRows[index][field] as typeof value) = value;

        if (field === 'name') {
            newRows[index].showSuggestions = (value as string).length > 0;
        }
        setRows(newRows);
    }

    const addNewRow = (): void => {
        setRows([...rows, { id: Date.now() + Math.random(), name: '', qty: 1, showSuggestions: false, barcode: '' }]);
    }

    const removeRow = (index: number): void => {
        if (rows.length > 1) {
            setRows(rows.filter((_, i) => i !== index));
        }
    }

    const selectItem = (index: number, item: Item): void => {
        const updatedRow = {
            ...rows[index],
            name: item.item_name,
            showSuggestions: false,
            barcode: item?.barcode
        };

        const newRows = [...rows];
        newRows[index] = updatedRow;
        if (index == rows?.length - 1) {
            setRows([...newRows,
            { id: Date.now() + Math.random(), name: '', qty: 1, showSuggestions: false, barcode: '' }]);
        }
        else {
            setRows(newRows);
        }
    };

    const printItems = () => {
        const validRows = rows?.filter(row => row.name?.trim() !== '')
        if (validRows && validRows.length > 0) {
            localStorage.setItem('items', JSON.stringify(validRows));
            navigate('/print_labels');
        }
    }

    return (
        <MainLayout>
            <Header title={`${t('Create Labels')}`} info={``} />

            <div className="mt-4" dir="rtl">
                <div className="max-w-5xl mx-auto">
                    <header className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">{t("Add Items")}</h1>
                            <p className="text-slate-500 text-sm">{t("Enter items and specify required quantities")}</p>
                        </div>
                    </header>

                    <div className="space-y-4 mb-8">
                        {rows.map((row, index) => (
                            <div
                                key={row.id}
                                className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 hover:border-emerald-300 transition-colors group"
                            >
                                <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
                                    <div className="hidden md:flex bg-slate-100 text-slate-500 w-8 h-8 rounded-full items-center justify-center text-xs font-bold mb-1">
                                        {index + 1}
                                    </div>

                                    <div className="flex-1 w-full relative">
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t("Item Name")}</label>
                                        <input
                                            type="text"
                                            value={row.name}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange(index, 'name', e.target.value)}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                                            placeholder={t("Start typing to search...")}
                                        />

                                        {row.showSuggestions && (
                                            <div className="absolute z-20 w-full bg-white border border-slate-200 shadow-xl rounded-xl mt-2 max-h-52 overflow-y-auto overflow-x-hidden">
                                                {items.filter(p => p.item_name.toLowerCase().includes(row.name.toLowerCase())).length > 0 ? (
                                                    items
                                                        .filter(p => p.item_name.toLowerCase().includes(row.name.toLowerCase()))
                                                        .map((item) => (
                                                            <div
                                                                key={item.item_id}
                                                                className="px-4 py-3 hover:bg-emerald-50 cursor-pointer border-b border-slate-50 last:border-0 text-sm flex justify-between items-center"
                                                                onClick={() => selectItem(index, item)}
                                                            >
                                                                <span className="font-medium">{item.item_name}</span>
                                                            </div>
                                                        ))
                                                ) : (
                                                    <div className="px-4 py-3 text-sm text-slate-400">{t("No results found...")}</div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="w-full md:w-32">
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t("Quantity")}</label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={row.qty}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange(index, 'qty', parseInt(e.target.value) || 0)}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-center font-bold focus:bg-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                                        />
                                    </div>

                                    <button
                                        onClick={() => removeRow(index)}
                                        className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                        title={t("Delete Row")}
                                    >
                                        <Trash className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        ))}

                        <button
                            onClick={addNewRow}
                            className="w-full py-4 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50/50 transition-all flex items-center justify-center gap-2 font-medium"
                        >
                            <Plus className="w-5 h-5" />
                            {t("Add Item")}
                        </button>
                    </div>

                    <footer className="sticky bottom-8 bg-white/80 backdrop-blur-md p-4 rounded-2xl shadow-xl border border-slate-200 flex flex-wrap justify-center md:justify-end gap-4">
                        <button
                            onClick={printItems}
                            className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-slate-300 font-medium hover:bg-slate-50 transition-all">
                            <Printer className="w-5 h-5" />
                            {t("Print Labels")}
                        </button>
                    </footer>
                </div>
            </div>
        </MainLayout>


    );
}

export default CreateLabels;